import {
  FC,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
  useState,
} from 'react';

import {
  CheckOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LinkOutlined,
  LockOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { copyToClipboard } from '../../../../../../helpers/utils/safeClipboard';
import {
  Button,
  Popconfirm,
  Popover,
  PopoverProps,
  Tooltip,
} from 'antd';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { AccesModal, AddReportTGModal, EditModal, InternalTagAttachModal, ModalKey, ThunkDispatch } from './modals';
import {
  copyReportOrDashboard,
  setDefaultReportThunk,
  toggleFavorite,
} from '../../../../../../app/providers/redux/slices/reports/params/reports/reportsSlice';
import { ROLES } from '../../../../../../helpers/utils/auth';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../../../helpers/utils/ui';
import { unApi } from '../../../../api/endpoints/reports/unApi';
import { QUERY_NAME_DASHBOARD } from '../../../../constants/query-params';
import { routeNames } from '../../../../constants/routes';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import EditReportTG from '../../../reports/un-title/components/modals/EditReportTG/EditReportTG';
import { getRouteByReportId, ItemTypee, KEY_COHGORT, KEY_WEBINAR_QUALITY } from '../TabContent';
import { CONST_HASH_MAIN_DASHBOARD } from '../../../../constants/staticLinkDashboard';

const modalButtonStyle: HTMLAttributes<HTMLButtonElement>['style'] = {
  outline: 'none',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
  transition: 'background 0.3s',
  boxShadow: 'none'
};

interface PopoverContentProps {
  item: ItemTypee;
  isDashboard: boolean;
  trigger?: PopoverProps['trigger']
  isHaveTaskReportInTg?: boolean
  setReloadTask?: () => void
}

interface MenuItem<T = any> {
  key: ModalKey;
  text: ReactNode;
  tooltipText?: string
  Icon: FC<any>;
  onClick?: MouseEventHandler<HTMLElement>;
  modal?: FC<T>;
  modalProps?: T;
  disabled?: boolean;
  isHaveTaskReportInTg?: boolean;
}

const MenuItem: FC<MenuItem> = ({
  text,
  tooltipText = 'Недостаточно прав',
  Icon,
  onClick,
  disabled = false,
  isHaveTaskReportInTg,
}) => {
  return (
    <Tooltip title={disabled ? tooltipText : ''} trigger={'hover'} placement='right'>
      <Button
        style={{ ...modalButtonStyle, color: isHaveTaskReportInTg? '#42aaff' : '' }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        disabled={disabled}
      >
        <Icon style={{ fontSize: 15, marginRight: '8px' }} />
          <span style={{ fontSize: 15 }}>{text} {disabled && <LockOutlined />}</span>
      </Button>
    </Tooltip>
  );
};

const renderMenuItem = (props: (MenuItem | null)) => (
  props ? <MenuItem {...props} key={props.key} /> : null
);

const SuperPermission = new Set([ ROLES.owner, ROLES.employee, ROLES.super ]);

export const ReportMenu: FC<PopoverContentProps> = ({ item, isDashboard, trigger = 'click', isHaveTaskReportInTg = false, setReloadTask }) => {
  const dispatch: ThunkDispatch = useDispatch();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const { savedDashboards, savedReports } = useTypedSelector((state) => state.reports);
  const {
    setReportFavorite,
    setDashboardFavorite,
    setSavedReports,
    setSavedDashboards,
    deleteReport
  } = useActions();

  const schoolId = useTypedSelector(state => state.currentSchool.selectedSchool?.id);
  const { role: currentUserRole, id: userId, isContact } = useTypedSelector(state => state.currentUser);
  const isCompare = useTypedSelector(state => state.meta.isCompare);
  const mainHash = useTypedSelector(state => state.reports.mainDashboardHash);
  
  const [ modalVisible, setModalVisible ] = useState<
  Partial<Record<ModalKey, boolean>>
  >({
    [ModalKey.Rename]: false,
    [ModalKey.Tags]: false,
    [ModalKey.SendTG]: false,
    [ModalKey.Accesses]: false,
  });
  const [ isOpen, setIsOpen ] = useState(false);
  
  const adminPermission = currentUserRole === ROLES.owner || currentUserRole === ROLES.super
    || (currentUserRole === ROLES.employee && isContact);
  const canDoAction = item.user_id === userId || adminPermission;

  const itSCANReport = item.access_id === 3;
  const havePerrmissonToSCAN = itSCANReport ? currentUserRole === ROLES.super : true;
  const isStaticReport = !!item.static;
  
  const handleToggleFavorite: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    const newFavoriteStatus = !item.is_favorite;
    const setFavorite =
      isDashboard ? setDashboardFavorite : setReportFavorite;

    setFavorite({
      id: item.id,
      isFavorite: newFavoriteStatus,
    });

    dispatch(toggleFavorite({ id: item.id, isFavorite: item.is_favorite }))
      .unwrap()
      .catch(() => {
        setFavorite({
          id: item.id,
          isFavorite: !newFavoriteStatus,
        });
      });
  };
  const handeCopyReport: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    const encodedHash = encodeURIComponent(item.hash);
    let reportLink = `${process.env.REACT_APP_BASE_URL}${location.pathname}?report_id=${encodedHash}`;
    
    if (item.id === KEY_COHGORT) {
      reportLink = `${process.env.REACT_APP_BASE_URL}${routeNames.reportCohort}`;
    }
    if (item.id === KEY_WEBINAR_QUALITY) {
      reportLink = `${process.env.REACT_APP_BASE_URL}${routeNames.reportWebinarQuality}`;
    }

    copyToClipboard(reportLink).then((success) => {
      if (success) {
        showSuccessMessage('Ссылка скопирована');
      } else {
        showErrorMessage('Ошибка при копировании ссылки отчета');
      }
    });
  };
  const handleCopyReportOrDashboard: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();

    if (!schoolId) {
      showErrorMessage('Школа не выбрана');
      return;
    }

    unApi.getReportDataFromHash(item.hash)
    .then((response) => {
      const values = response.data[0]?.values;
      const newName = `${item.name || values?.name || 'Отчёт'} (копия)`;

      const link = isDashboard? '/dashboards' : getRouteByReportId(item.report_id);

      dispatch(
        copyReportOrDashboard({
          schoolId,
          id: item.id,
          isDashboard,
          newName,
          values: values,
          pathName: location.origin + link,
          callBack: (responseData: any) => {
            setIsOpen(false);
            const newItem = {
              ...item,
              id: responseData.id || item.id,
              name: newName,
              hash: responseData.hash,
              access_id: 1,
              is_favorite: false,
              user_id: userId,
            };
            dispatch(isDashboard? setSavedDashboards([ ...savedDashboards, newItem ]) : setSavedReports([ ...savedReports, newItem ]));
            showSuccessMessage(`${isDashboard? 'Дашборд' : 'Отчет'} успешно дублирован`);
          }
        })
      );
    })
    .catch(() => {
            showErrorMessage(
              isDashboard
                ? 'Ошибка дублирования дашборда'
                : 'Ошибка дублирования отчёта'
            );
    });
  };

  const handleRemoveReport = () => {
    if (!isCompare && schoolId) {

      deleteReport(schoolId, {
          id: item.id,
          hash: item.hash,
          urlHash: searchParams.get(QUERY_NAME_DASHBOARD)
        });
    }
  };

  const handleAddAtMain = () => {
    if (mainHash !== item.hash) {
      dispatch(setDefaultReportThunk({ hash: item.hash, id: item.id }));
    }
  };


  const Actions: (MenuItem | null)[] = [
    !isStaticReport ?{
      key: ModalKey.Favorite,
      text: item?.is_favorite ? 'Убрать из избранного' : 'В избранное',
      Icon: item?.is_favorite ? StarFilled : StarOutlined,
      onClick: handleToggleFavorite,
    } : null,
    havePerrmissonToSCAN && !isStaticReport ? {
      key: ModalKey.Rename,
      text: 'Переименовать',
      Icon: EditOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.Rename]: true }));
        setIsOpen(false);
      },
      disabled: !canDoAction || !havePerrmissonToSCAN,
      modal: EditModal,
      modalProps: {
        item,
        isDashboard,
      },
    } : null,
    {
      key: ModalKey.CopyLink,
      text: 'Копировать ссылку',
      Icon: LinkOutlined,
      onClick: handeCopyReport,
    },
    !isStaticReport ? {
      key: ModalKey.CopyData,
      text: isDashboard ? 'Дублировать дашборд' : 'Дублировать отчёт',
      Icon: CopyOutlined,
      onClick: handleCopyReportOrDashboard,
    } : null,
    havePerrmissonToSCAN && !isStaticReport ? {
      key: ModalKey.Tags,
      text: 'Теги',
      Icon: TagOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.Tags]: true }));
        setIsOpen(false);
      },
      modal: InternalTagAttachModal,
      disabled: !adminPermission || !havePerrmissonToSCAN,
      modalProps: {
        isDashboard,
        item,
      },
    } : null,
    havePerrmissonToSCAN ? {
      key: ModalKey.Accesses,
      text: 'Доступ',
      Icon: LockOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.Accesses]: true }));
        setIsOpen(false);
      },
      disabled: !adminPermission || !havePerrmissonToSCAN,
      modal: AccesModal,
      modalProps: {
        item,
        isDashboard,
      },
    } : null,
  ];

  const dashboardActions: MenuItem[] = [
    item.hash !== mainHash ? {
      key: ModalKey.Add,
      text: 'Добавить в сводку',
      Icon: PlusOutlined,
      onClick: handleAddAtMain,
    } : {
      key: ModalKey.Add,
      text: 'Уже в сводке',
      tooltipText: 'Уже в сводке',
      Icon: CheckOutlined,
      onClick: () => {},
      disabled: true,
    }
  ];

  const reportActions: (MenuItem & {isHaveTaskReportInTg?: boolean} | null)[] = [
    !isStaticReport ? {
      key: ModalKey.SendTG,
      text: 'Отправка в TG',
      isHaveTaskReportInTg,
      Icon: ClockCircleOutlined,
      onClick: () =>{
        setModalVisible((prev) => ({ ...prev, [ModalKey.SendTG]: true }));
        setIsOpen(false);
      },
      modal: isHaveTaskReportInTg ? EditReportTG : AddReportTGModal,
      modalProps: {
        report: {
          visible: false,
          title: item?.name,
          id: item?.id,
        },
      },
    } : null,
  ];

  const removeAction = {
    key: ModalKey.RemoveAction,
    text: 'Удалить',
    Icon: DeleteOutlined,
    disabled: !canDoAction || !havePerrmissonToSCAN,
  };

  const currentActions = isDashboard ? dashboardActions : reportActions;
  const allActions = [ ...Actions, ...currentActions ];
  const menuActions = allActions.map(renderMenuItem);
  const modalActions = allActions.map((Action) => {
    if (!Action) return null;

    const Modal = Action.modal;
    return Modal ? (
      <Modal
        {...Action.modalProps}
        visible={modalVisible[Action.key]}
        isVisible={modalVisible[Action.key]}
        setReloadTask={setReloadTask}
        setVisible={(visible: any) =>
          setModalVisible((prev) => ({ ...prev, [Action.key]: visible?.visible ?? visible }))
        }
        setRefetch={() => null}
      />
    ) : null;
  });

  const isVisible = Object.values(modalVisible).some(Boolean)
    ? false
    : true;
    
  return (
    <>{isVisible && 
      <Popover
        content={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {menuActions}
            {havePerrmissonToSCAN ? <Popconfirm
              title="Вы уверены, что хотите удалить?"
              onConfirm={handleRemoveReport}
              okText="Удалить"
              cancelText="Отмена"
              placement="top"
            >
              {renderMenuItem(removeAction)}
            </Popconfirm> : null}
          </div>
        }
        // placement="bottomRight"
        style={{ borderRadius: '10px' }}
        trigger={trigger}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <div
          style={{ marginLeft: '10px', cursor: 'pointer' }}
        >
          <EllipsisOutlined />
        </div>
      </Popover>}
      {modalActions}
    </>
  );
};
